import React, { memo, useMemo } from 'react';
import { UspSimple } from '~/lib/data-contract';
import { Image, Tooltip, Text, CMSLink } from '~/shared/components';
import { TextProps, TextShade } from '../Text';
import { StyledImageWrapper, StyledUspText } from './styled';
import parse from 'html-react-parser';
import clsx from 'clsx';
import styles from './usp.module.css';

export type Props = {
    uspItem?: UspSimple;
    shade?: TextShade | undefined;
    textDecoration?: string;
    tooltipPosition?: React.ComponentProps<typeof Tooltip>['position'];
    className?: string;
} & Pick<TextProps, 'variant'>;

export const Usp = memo(
    ({ uspItem, shade, variant = 'body', tooltipPosition = 'bottom', className }: Props) => {
        const { link, text, image, description } = uspItem ?? {};

        const parsedDescription = useMemo(() => {
            if (description) {
                return parse(description);
            }

            return null;
        }, [description]);

        if (!uspItem) {
            return null;
        }

        return (
            <Tooltip
                content={
                    parsedDescription ? (
                        <Text as="div" variant="caption">
                            {parsedDescription}
                        </Text>
                    ) : undefined
                }
                style={{ whiteSpace: 'nowrap' }}
                distance={155}
                describedBy="usp-tooltip"
                position={tooltipPosition}
                disableOnSm
            >
                <CMSLink
                    href={link?.url}
                    prefetch={false}
                    tabIndex={link?.url ? undefined : 0}
                    className={clsx(styles.rootLink, className)}
                >
                    {image?.src && (
                        <StyledImageWrapper data-image-wrapper>
                            <Image
                                src={image.src}
                                alt={image.alt ?? ''}
                                height={18}
                                width={18}
                                sizes="18px"
                                mimeType="image/svg+xml"
                                skeletonShade="none"
                                showSpinner={false}
                                invert={
                                    shade === 'paragraphInverted' || shade === 'headlineInverted'
                                }
                            />
                        </StyledImageWrapper>
                    )}
                    <StyledUspText
                        as="span"
                        variant={variant}
                        shade={shade}
                        style={{ color: shade ? undefined : 'currentColor' }}
                    >
                        {link?.text || text}
                    </StyledUspText>
                </CMSLink>
            </Tooltip>
        );
    }
);
